import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Settings2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CostCentresTable, DEFAULT_COST_CENTRE_COLUMNS } from "@/components/blocks/cost-centres/cost-centres-table";
import type { CostCentreColumnDefinition } from "@/components/blocks/cost-centres/cost-centres-table";
import { CostCentresHierarchicalTable } from "@/components/blocks/cost-centres/cost-centres-hierarchical-table";
import { useProjectDetailsContext } from "../context";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";
import { usePermissions } from "@/contexts/PermissionsContext";
import { refreshCostCentresFromFinance, fetchCostCentreStructureByProjectId, getDataMode } from "@/lib/data-service";
import { featureFlags } from "@/lib/feature-flags";

// Keys that are only meaningful when budget values exist (Cost Centre mode)
export const BUDGET_ONLY_COLUMN_KEYS: string[] = [
  'costsBudget',
  'costVariance',
  'costPercent',
  'remainingCosts',
  'forecastCosts',
  'revenueBudget',
  'revenueVariance',
  'revenuePercent',
  'remainingRevenue',
  'forecastRevenue',
  'hoursBudget',
  'hoursVariance',
  'hoursPercent',
  'remainingHours',
];

function buildColumns(isCostCentreMode: boolean): CostCentreColumnDefinition[] {
  return DEFAULT_COST_CENTRE_COLUMNS.map((col) => {
    const isBudgetOnlyCol = BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string);
    if (isBudgetOnlyCol) {
      // Budget/variance/% columns only visible in Cost Centre mode
      return { ...col, visible: isCostCentreMode && col.visible };
    }
    // Actuals + identification columns — keep their default visibility
    return { ...col };
  });
}

export function CostCentresTab() {
  const ctx = useProjectDetailsContext();
  const {
    costCentres,
    costCentresLoading,
    setCostCentresLoading,
    numericProjectId,
    hierarchicalCostCentres,
    setHierarchicalCostCentres,
    costCentreColumns,
    setCostCentreColumnConfigOpen,
    isStructureView,
    setIsStructureView,
    setSelectedCostCentre,
    setCostCentreDetailsPanelOpen,
    setCreateCostCentrePanelOpen,
    setConfigureStructureOpen,
    setCostCentres,
  } = ctx;

  const { isCostCentreMode } = useBudgetPermissions();
  const { canPerformAction } = usePermissions();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Build mode-aware column set — override visibility based on budget mode.
  // If the user has customised columns via costCentreColumns (from context), respect that
  // but still enforce budget-column visibility rules.
  const modeAwareColumns = useMemo<CostCentreColumnDefinition[]>(() => {
    const base = costCentreColumns.length > 0 ? costCentreColumns : buildColumns(isCostCentreMode);
    return base.map((col) => {
      const isBudgetOnlyCol = BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string);
      if (isBudgetOnlyCol && !isCostCentreMode) {
        // Force-hide budget-only columns when not in Cost Centre mode
        return { ...col, visible: false };
      }
      return col;
    });
  }, [costCentreColumns, isCostCentreMode]);

  const handleRefreshAllFromFinance = async () => {
    const allIds = costCentres.map((cc) => cc.id).filter((id): id is number => typeof id === 'number');
    if (allIds.length === 0) {
      toast.info('No cost centres to refresh.');
      return;
    }

    setIsRefreshing(true);
    try {
      const result = await refreshCostCentresFromFinance(allIds);
      const notReconciled =
        (result.unlinked ?? 0) + (result.sourceNotFound ?? 0) + (result.conflicts ?? 0) + (result.failed ?? 0);

      const summary = `Finance refresh complete: ${result.updated} updated, ${result.unchanged} unchanged, ${result.unlinked} unlinked, ${result.sourceNotFound} not found, ${result.conflicts} conflicts, ${result.failed} failed`;
      const description = `${allIds.length} cost centre${allIds.length === 1 ? '' : 's'} checked`;

      if (notReconciled > 0) {
        toast.warning(summary, { description });
      } else {
        toast.success(summary, { description });
      }

      if (result.cappedAt200) {
        toast.info('Only the first 200 Cost Centres were synced.');
      }

      // Refetch the cost centres so the grid reflects any code/name corrections.
      if (numericProjectId && getDataMode() === 'live') {
        setCostCentresLoading(true);
        try {
          const freshCostCentres = await fetchCostCentreStructureByProjectId(numericProjectId);
          setCostCentres(freshCostCentres.filter(cc => !cc.isStructureNode));
          setHierarchicalCostCentres(freshCostCentres);
        } catch (error) {
          console.error('Failed to refetch cost centres:', error);
        } finally {
          setCostCentresLoading(false);
        }
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to refresh cost centres from Finance.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      {/* Header Section */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 ml-auto">
          {featureFlags.isEnabled('sourceReconciliation') && canPerformAction('edit') && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshAllFromFinance}
                    disabled={isRefreshing || costCentresLoading}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Refresh All CostCentres from Finance</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCostCentreColumnConfigOpen(true)}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            Configure Columns
          </Button>
          {!isStructureView && (
            <Button size="sm" onClick={() => setCreateCostCentrePanelOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              New
            </Button>
          )}
          {isStructureView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigureStructureOpen(true)}
              className="gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Configure Structure
            </Button>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
              Structure View
            </label>
            <Switch
              id="structure-view-toggle"
              checked={isStructureView}
              onCheckedChange={setIsStructureView}
            />
          </div>
        </div>
      </div>

      {/* Content Display Area */}
      {isStructureView ? (
        <CostCentresHierarchicalTable
          data={hierarchicalCostCentres}
          columns={modeAwareColumns}
          loading={costCentresLoading}
          useCardWrapper={false}
          onRowClick={(costCentre) => {
            setSelectedCostCentre(costCentre);
            setCostCentreDetailsPanelOpen(true);
          }}
        />
      ) : (
        <CostCentresTable
          data={costCentres}
          columns={modeAwareColumns}
          loading={costCentresLoading}
          useCardWrapper={false}
          onRowClick={(costCentre) => {
            setSelectedCostCentre(costCentre);
            setCostCentreDetailsPanelOpen(true);
          }}
        />
      )}
    </>
  );
}
